import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { API_CONFIG, ChatResponse, MessageResponse, WebSocketMessage, WebSocketResponse } from '../config/api';
import './Chat.css';

type Message = {
  id: string;
  sender: string;
  content: string;
  timestamp: string;
  role: 'user' | 'assistant' | 'system';
};

type Conversation = {
  id: string;
  chat_id: string;
  title: string;
  messages: Message[];
  createdAt: string;
  updatedAt: string;
  is_active: boolean;
};

type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

const Chat: React.FC = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showSidebar, setShowSidebar] = useState(window.innerWidth > 900);
  const [connectionRetries, setConnectionRetries] = useState(0);
  const [maxRetries] = useState(3);
  const ws = useRef<WebSocket | null>(null);
  const chatWindowRef = useRef<HTMLDivElement>(null);
  const { user, signOut, getAuthToken, apiToken } = useAuth();
  const navigate = useNavigate();

  // Get current conversation
  const currentConversation = conversations.find(conv => conv.chat_id === currentConversationId);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatWindowRef.current) {
      chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
    }
  }, [currentConversation?.messages]);

  // Add event listener to close sidebar on resize for responsiveness
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 900) {
        setShowSidebar(false);
      } else {
        setShowSidebar(true);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadConversations = useCallback(async () => {
    try {
      const response = await apiService.getUserChats(user!.username);
      const conversations: Conversation[] = response.chats.map((chat: ChatResponse) => ({
        id: chat.chat_id,
        chat_id: chat.chat_id,
        title: chat.title || 'New Chat',
        messages: [],
        createdAt: chat.created_at,
        updatedAt: chat.updated_at,
        is_active: chat.is_active,
      }));
      
      setConversations(conversations);
      if (conversations.length > 0 && !currentConversationId) {
        setCurrentConversationId(conversations[0].chat_id);
        loadChatMessages(conversations[0].chat_id);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  }, [user, currentConversationId]);

  // Load conversations from API
  useEffect(() => {
    if (apiToken && user?.username) {
      loadConversations();
    }
  }, [apiToken, user?.username, loadConversations]);

  const loadChatMessages = async (chatId: string) => {
    try {
      const response = await apiService.getChatMessages(chatId);
      const messages: Message[] = response.messages.map((msg: MessageResponse) => ({
        id: msg.id.toString(),
        sender: msg.role === 'user' ? 'User' : 'Assistant',
        content: msg.content,
        timestamp: msg.created_at,
        role: msg.role,
      }));

      setConversations(prev => 
        prev.map(conv => 
          conv.chat_id === chatId 
            ? { ...conv, messages }
            : conv
        )
      );
    } catch (error) {
      console.error('Error loading chat messages:', error);
    }
  };

  const createNewChat = useCallback(async () => {
    if (!user?.username) return;

    try {
      const chatId = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const chatData = {
        chat_id: chatId,
        user_id: user.username,
        title: 'New Chat',
      };

      const response = await apiService.createChat(user.username, chatData);
      
      const newConversation: Conversation = {
        id: response.chat_id,
        chat_id: response.chat_id,
        title: response.title || 'New Chat',
        messages: [],
        createdAt: response.created_at,
        updatedAt: response.updated_at,
        is_active: response.is_active,
      };
      
      setConversations(prev => [newConversation, ...prev]);
      setCurrentConversationId(newConversation.chat_id);
      setInput('');
    } catch (error) {
      console.error('Error creating new chat:', error);
    }
  }, [user?.username]);

  const deleteConversation = async (chatId: string) => {
    try {
      await apiService.deleteChat(chatId);
      setConversations(prev => prev.filter(conv => conv.chat_id !== chatId));
      
      // If we're deleting the current conversation, switch to the first available one
      if (currentConversationId === chatId) {
        const remainingConversations = conversations.filter(conv => conv.chat_id !== chatId);
        if (remainingConversations.length > 0) {
          setCurrentConversationId(remainingConversations[0].chat_id);
          loadChatMessages(remainingConversations[0].chat_id);
        } else {
          createNewChat();
        }
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  const updateChatTitle = async (chatId: string, title: string) => {
    try {
      await apiService.updateChat(chatId, { title });
      setConversations(prev => 
        prev.map(conv => 
          conv.chat_id === chatId 
            ? { ...conv, title, updatedAt: new Date().toISOString() }
            : conv
        )
      );
    } catch (error) {
      console.error('Error updating chat title:', error);
    }
  };

  const getConversationTitle = (conversation: Conversation): string => {
    if (conversation.title !== 'New Chat') {
      return conversation.title;
    }
    
    // Generate title from first user message
    const firstUserMessage = conversation.messages.find(msg => msg.role === 'user');
    if (firstUserMessage) {
      const title = firstUserMessage.content.slice(0, 50);
      return title.length === 50 ? title + '...' : title;
    }
    
    return 'New Chat';
  };

  useEffect(() => {
    const connectWebSocket = async () => {
      // Don't attempt connection if we've exceeded max retries
      if (connectionRetries >= maxRetries) {
        console.log('Max connection retries exceeded, stopping reconnection attempts');
        setConnectionStatus('error');
        return;
      }

      try {
        setConnectionStatus('connecting');
        
        // Get authentication token
        const token = await getAuthToken();
        if (!token) {
          console.error('No authentication token available');
          setConnectionStatus('error');
          return;
        }

        // Connect to WebSocket with token as query parameter
        const wsUrl = `${API_CONFIG.WEBSOCKET_URL}?token=${encodeURIComponent(token)}`;
        console.log(`Attempting to connect to WebSocket (attempt ${connectionRetries + 1}/${maxRetries}):`, wsUrl);
        
        ws.current = new window.WebSocket(wsUrl);
        
        ws.current.onopen = () => {
          console.log('WebSocket connected successfully');
          setConnectionStatus('connected');
          setConnectionRetries(0); // Reset retries on successful connection
        };
        
        ws.current.onmessage = (event) => {
          try {
            const rawData = JSON.parse(event.data);
            console.log('WebSocket message received:', rawData);
            
            // Filter out connection status messages
            if (rawData.type === 'connection' || rawData.type === 'status') {
              console.log('Ignoring connection/status message:', rawData);
              return;
            }
            
            // Only process chat response messages
            if (!rawData.status || !rawData.result) {
              console.log('Ignoring non-chat message:', rawData);
              return;
            }
            
            const data: WebSocketResponse = rawData;
            setIsLoading(false);
            console.log('Processing chat message:', data.status, data.result?.substring(0, 50) + '...');
            console.log('Current conversation ID:', currentConversationId);
            console.log('Current conversations:', conversations.length);
            
            if (data.status === 'streaming') {
              console.log('Processing streaming message');
              // For streaming, always add a new assistant message if none exists
              setConversations(prev => {
                const updated = prev.map(conv => {
                  if (conv.chat_id === currentConversationId) {
                    const hasAssistantMessage = conv.messages.some(msg => msg.role === 'assistant');
                    console.log('Has assistant message:', hasAssistantMessage, 'Messages count:', conv.messages.length);
                    
                    if (hasAssistantMessage) {
                      // Update existing assistant message
                      return {
                        ...conv,
                        messages: conv.messages.map(msg => 
                          msg.role === 'assistant' && msg.sender === 'Assistant'
                            ? { ...msg, content: data.result }
                            : msg
                        ),
                        updatedAt: new Date().toISOString()
                      };
                    } else {
                      // Create new assistant message
                      const newMessage: Message = {
                        id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                        sender: 'Assistant',
                        content: data.result,
                        timestamp: new Date().toISOString(),
                        role: 'assistant',
                      };
                      console.log('Creating new assistant message:', newMessage);
                      return {
                        ...conv,
                        messages: [...conv.messages, newMessage],
                        updatedAt: new Date().toISOString()
                      };
                    }
                  }
                  return conv;
                });
                console.log('Updated conversations:', updated);
                return updated;
              });
            } else if (data.status === 'completed') {
              console.log('Final message received, updating conversation');
              // Final message - ensure we have the complete response
              setConversations(prev => {
                const updated = prev.map(conv => {
                  if (conv.chat_id === currentConversationId) {
                    const hasAssistantMessage = conv.messages.some(msg => msg.role === 'assistant');
                    console.log('Completed - Has assistant message:', hasAssistantMessage);
                    
                    if (hasAssistantMessage) {
                      // Update existing assistant message
                      return {
                        ...conv,
                        messages: conv.messages.map(msg => 
                          msg.role === 'assistant' && msg.sender === 'Assistant'
                            ? { ...msg, content: data.result }
                            : msg
                        ),
                        updatedAt: new Date().toISOString(),
                        title: getConversationTitle({ 
                          ...conv, 
                          messages: conv.messages.map(msg => 
                            msg.role === 'assistant' && msg.sender === 'Assistant'
                              ? { ...msg, content: data.result }
                              : msg
                          )
                        })
                      };
                    } else {
                      // Create new assistant message if none exists
                      const newMessage: Message = {
                        id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                        sender: 'Assistant',
                        content: data.result,
                        timestamp: new Date().toISOString(),
                        role: 'assistant',
                      };
                      console.log('Creating new assistant message for completed:', newMessage);
                      return {
                        ...conv,
                        messages: [...conv.messages, newMessage],
                        updatedAt: new Date().toISOString(),
                        title: getConversationTitle({ 
                          ...conv, 
                          messages: [...conv.messages, newMessage]
                        })
                      };
                    }
                  }
                  return conv;
                });
                console.log('Updated conversations for completed:', updated);
                return updated;
              });

              // Update chat title if this is the first assistant response
              if (currentConversationId) {
                const conversation = conversations.find(conv => conv.chat_id === currentConversationId);
                if (conversation && conversation.messages.length === 2) { // User message + first assistant response
                  const newTitle = conversation.messages[0].content.slice(0, 50);
                  updateChatTitle(currentConversationId, newTitle.length === 50 ? newTitle + '...' : newTitle);
                }
              }
            } else if (data.status === 'failed') {
              console.log('Error message received:', data.result);
              // Handle error
              const errorMessage: Message = {
                id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                sender: 'System',
                content: `Error: ${data.result}`,
                timestamp: new Date().toISOString(),
                role: 'system',
              };
              
              setConversations(prev => 
                prev.map(conv => 
                  conv.chat_id === currentConversationId
                    ? {
                        ...conv,
                        messages: [...conv.messages, errorMessage],
                        updatedAt: new Date().toISOString()
                      }
                    : conv
                )
              );
            }
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };
        
        ws.current.onclose = (event) => {
          console.log('WebSocket closed:', event.code, event.reason);
          setConnectionStatus('disconnected');
          setIsLoading(false);
          
          // Increment retry counter
          setConnectionRetries(prev => prev + 1);
          
          // Show user-friendly error message only on first failure
          if (connectionRetries === 0) {
            const errorMessage: Message = {
              id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              sender: 'System',
              content: '⚠️ Connection lost. Please check your internet connection and try again.',
              timestamp: new Date().toISOString(),
              role: 'system',
            };
            
            setConversations(prev => 
              prev.map(conv => 
                conv.chat_id === currentConversationId
                  ? {
                      ...conv,
                      messages: [...conv.messages, errorMessage],
                      updatedAt: new Date().toISOString()
                    }
                  : conv
              )
            );
          }
        };
        
        ws.current.onerror = (error) => {
          console.error('WebSocket error:', error);
          setConnectionStatus('error');
          setIsLoading(false);
          
          // Increment retry counter
          setConnectionRetries(prev => prev + 1);
          
          // Show user-friendly error message only on first failure
          if (connectionRetries === 0) {
            const errorMessage: Message = {
              id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              sender: 'System',
              content: '❌ Failed to connect to chat server. Please check your internet connection and try again.',
              timestamp: new Date().toISOString(),
              role: 'system',
            };
            
            setConversations(prev => 
              prev.map(conv => 
                conv.chat_id === currentConversationId
                  ? {
                      ...conv,
                      messages: [...conv.messages, errorMessage],
                      updatedAt: new Date().toISOString()
                    }
                  : conv
            )
          );
          }
        };
      } catch (error) {
        console.error('Error connecting to WebSocket:', error);
        setConnectionStatus('error');
        setConnectionRetries(prev => prev + 1);
        
        // Show user-friendly error message only on first failure
        if (connectionRetries === 0) {
          const errorMessage: Message = {
            id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            sender: 'System',
            content: '❌ Unable to establish connection. Please check your authentication and ensure the API server is running.',
            timestamp: new Date().toISOString(),
            role: 'system',
          };
          
          setConversations(prev => 
            prev.map(conv => 
              conv.chat_id === currentConversationId
                ? {
                    ...conv,
                    messages: [...conv.messages, errorMessage],
                    updatedAt: new Date().toISOString()
                  }
                : conv
            )
          );
        }
      }
    };

    if (currentConversationId) {
      connectWebSocket();
    }
    
    return () => {
      if (ws.current) {
        ws.current.close();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAuthToken, currentConversationId, connectionRetries, maxRetries]);

  const sendMessage = async () => {
    if (input.trim() && ws.current?.readyState === WebSocket.OPEN && currentConversationId) {
      try {
        console.log('Sending message:', input);
        console.log('Current conversation ID:', currentConversationId);
        
        // Add user message to conversation
        const userMessage: Message = {
          id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          sender: 'User',
          content: input,
          timestamp: new Date().toISOString(),
          role: 'user',
        };
        
        console.log('Creating user message:', userMessage);
        
        setConversations(prev => {
          const updated = prev.map(conv => 
            conv.chat_id === currentConversationId
              ? {
                  ...conv,
                  messages: [...conv.messages, userMessage],
                  updatedAt: new Date().toISOString(),
                  title: getConversationTitle({ ...conv, messages: [...conv.messages, userMessage] })
                }
              : conv
          );
          console.log('Updated conversations after user message:', updated);
          return updated;
        });

        // Save message to API
        await apiService.addMessage(currentConversationId, {
          chat_id: currentConversationId,
          role: 'user',
          content: input,
        });
        
        // Send message to WebSocket
        const wsMessage: WebSocketMessage = {
          query: input,
          chat_id: currentConversationId,
          plan_mode: false,
        };
        
        console.log('Sending WebSocket message:', wsMessage);
        ws.current.send(JSON.stringify(wsMessage));
        setInput('');
        setIsLoading(true);
      } catch (error) {
        console.error('Error sending message:', error);
        const errorMessage: Message = {
          id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          sender: 'System',
          content: 'Error sending message. Please try again.',
          timestamp: new Date().toISOString(),
          role: 'system',
        };
        
        setConversations(prev => 
          prev.map(conv => 
            conv.chat_id === currentConversationId
              ? {
                  ...conv,
                  messages: [...conv.messages, errorMessage],
                  updatedAt: new Date().toISOString()
                }
              : conv
          )
        );
      }
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      setIsLoggingOut(false);
    }
  };

  const handleRetryConnection = () => {
    setConnectionRetries(0);
    setConnectionStatus('disconnected');
    if (ws.current) {
      ws.current.close();
    }
  };

  const getStatusColor = (status: ConnectionStatus) => {
    switch (status) {
      case 'connected': return '#4CAF50';
      case 'connecting': return '#FF9800';
      case 'disconnected': return '#F44336';
      case 'error': return '#F44336';
      default: return '#757575';
    }
  };

  const getStatusText = (status: ConnectionStatus) => {
    switch (status) {
      case 'connected': return 'Connected';
      case 'connecting': return 'Connecting...';
      case 'disconnected': return 'Disconnected';
      case 'error': return 'Connection Error';
      default: return 'Unknown';
    }
  };

  return (
    <div className="chat-container">
      {/* Sidebar overlay for mobile */}
      {showSidebar && window.innerWidth <= 900 && (
        <div className="sidebar-overlay" onClick={() => setShowSidebar(false)} />
      )}
      {/* Sidebar */}
      <div className={`chat-sidebar${showSidebar ? ' open' : ''}`}>
        <div className="sidebar-header">
          <button 
            className="new-chat-btn"
            onClick={createNewChat}
          >
            <span>+</span> New Chat
          </button>
          <button 
            className="sidebar-toggle"
            onClick={() => setShowSidebar(false)}
            title="Close menu"
          >
            <span>×</span>
          </button>
        </div>
        
        <div className="conversations-list">
          {conversations.map((conversation) => (
            <div 
              key={conversation.chat_id}
              className={`conversation-item ${conversation.chat_id === currentConversationId ? 'active' : ''}`}
              onClick={() => {
                setCurrentConversationId(conversation.chat_id);
                loadChatMessages(conversation.chat_id);
              }}
            >
              <div className="conversation-content">
                <div className="conversation-title">
                  {getConversationTitle(conversation)}
                </div>
                <div className="conversation-date">
                  {new Date(conversation.updatedAt).toLocaleDateString()}
                </div>
              </div>
              <button 
                className="delete-conversation-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteConversation(conversation.chat_id);
                }}
                title="Delete conversation"
              >
                🗑️
              </button>
            </div>
          ))}
        </div>
        
        <div className="sidebar-footer">
          <div className="user-info">
            <span className="username">
              {user?.username || 'User'}
            </span>
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="logout-button"
            >
              {isLoggingOut ? 'Logging Out...' : 'Logout'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="chat-main">
        <header className="chat-header">
          <div className="header-content">
            <button 
              className="sidebar-toggle-mobile"
              onClick={() => setShowSidebar(true)}
              title="Open menu"
              style={{ display: showSidebar ? 'none' : 'inline-block' }}
            >
              ☰
            </button>
            <h1>Arny Chat</h1>
            <div className="connection-status">
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: getStatusColor(connectionStatus),
                animation: connectionStatus === 'connecting' ? 'pulse 1.5s infinite' : 'none'
              }}></div>
              <span>{getStatusText(connectionStatus)}</span>
            </div>
          </div>
        </header>
        
        <div className="chat-content">
          {currentConversation ? (
            <>
              <div 
                className="chat-window" 
                ref={chatWindowRef}
                data-testid="chat-window"
              >
                {currentConversation.messages.length === 0 ? (
                  <div className="empty-chat">
                    <h2>Welcome to Arny Chat!</h2>
                    <p>Start a new conversation by typing a message below.</p>
                    {connectionStatus === 'error' && (
                      <div className="api-warning">
                        <p>⚠️ <strong>API Server Not Available</strong></p>
                        <p>Unable to connect to the chat server.</p>
                        <p>Please check your internet connection and try again.</p>
                        <button 
                          onClick={handleRetryConnection}
                          className="retry-button"
                        >
                          Retry Connection
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  currentConversation.messages.map((msg) => (
                    <div key={msg.id} className={`message ${msg.role === 'user' ? 'user' : 'agent'}`}>
                      <div className="message-content">
                        <strong>{msg.sender}:</strong> {msg.content}
                      </div>
                      <div className="timestamp">
                        {new Date(msg.timestamp).toLocaleTimeString()}
                      </div>
                    </div>
                  ))
                )}
                
                {/* Loading indicator */}
                {isLoading && (
                  <div className="loading-message">
                    <strong>Assistant:</strong>
                    <div className="loading-dots">
                      <span style={{ animationDelay: '0s' }}>.</span>
                      <span style={{ animationDelay: '0.2s' }}>.</span>
                      <span style={{ animationDelay: '0.4s' }}>.</span>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="chat-input">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder="Type your message..."
                  disabled={connectionStatus !== 'connected' || isLoading}
                />
                <button 
                  onClick={sendMessage}
                  disabled={connectionStatus !== 'connected' || isLoading || !input.trim()}
                >
                  Send
                </button>
              </div>
            </>
          ) : (
            <div className="no-conversation">
              <p>No conversation selected</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Chat; 